/**
 * Zod schemas for the coaching app - mirrors DRF serializers.
 */

import { z } from "zod";

export const CoachingModeSchema = z.enum([
  "pre_generation",
  "post_generation",
  "standalone",
]);
export type CoachingMode = z.infer<typeof CoachingModeSchema>;

export const CoachingStatusSchema = z.enum([
  "gap_analysis",
  "active",
  "paused",
  "completed",
  "abandoned",
]);
export type CoachingStatus = z.infer<typeof CoachingStatusSchema>;

export const AffordanceHintSchema = z.enum([
  "textarea",
  "chips",
  "slider",
  "multi_select",
]);
export type AffordanceHint = z.infer<typeof AffordanceHintSchema>;

export const QuestionFinalStatusSchema = z.enum([
  "pending",
  "answered",
  "skipped",
  "declined",
  "dont_know",
  "abandoned",
]);
export type QuestionFinalStatus = z.infer<typeof QuestionFinalStatusSchema>;

export const CoachingResponseSchema = z.object({
  uuid: z.string().uuid(),
  turn_number: z.number().int().min(1).max(3),
  parent_response: z.string().uuid().nullable(),
  response_payload: z.record(z.unknown()),
  raw_user_text: z.string(),
  triggered_follow_up: z.boolean(),
  ambiguity_score: z.number().nullable(),
  was_final_in_question: z.boolean(),
  follow_up_question_text: z.string().nullable(),
  created_at: z.string(),
  interpretation_completed_at: z.string().nullable(),
});
export type CoachingResponse = z.infer<typeof CoachingResponseSchema>;

export const CoachingQuestionSchema = z.object({
  uuid: z.string().uuid(),
  display_order: z.number().int(),
  priority: z.string(),
  question_type: z.string(),
  prompt_text: z.string(),
  affordance_hint: AffordanceHintSchema.catch("textarea"),
  final_status: QuestionFinalStatusSchema,
  final_status_at: z.string().nullable(),
  shown_at: z.string().nullable(),
  responses: z.array(CoachingResponseSchema),
});
export type CoachingQuestion = z.infer<typeof CoachingQuestionSchema>;

export const CoachingSessionSchema = z.object({
  uuid: z.string().uuid(),
  mode: CoachingModeSchema,
  status: CoachingStatusSchema,
  tier_at_start: z.string(),
  max_questions: z.number().int(),
  target_job_ad: z.string().uuid().nullable(),
  started_at: z.string().nullable(),
  gap_analysis_completed_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  total_questions_asked: z.number().int(),
  total_questions_answered: z.number().int(),
  total_questions_skipped: z.number().int(),
  total_questions_declined: z.number().int(),
  total_questions_dont_know: z.number().int(),
  profile_updates_count: z.number().int(),
  match_score_before: z.number().nullable(),
  match_score_after: z.number().nullable(),
  effectiveness_score: z.number().nullable(),
  created_at: z.string(),
});
export type CoachingSession = z.infer<typeof CoachingSessionSchema>;

export const ProfileUpdateSummarySchema = z.object({
  update_kind: z.string(),
  after_payload: z.record(z.unknown()),
  user_accepted: z.boolean(),
});
export type ProfileUpdateSummary = z.infer<typeof ProfileUpdateSummarySchema>;

export const CoachingSessionSummarySchema = z.object({
  session_uuid: z.string().uuid(),
  profile_updates: z.array(ProfileUpdateSummarySchema),
  match_score_delta: z.number().nullable(),
  skills_added: z.number().int(),
  skills_confirmed: z.number().int(),
  accomplishments_edited: z.number().int(),
  accomplishments_created: z.number().int(),
  edit_proposals_pending_review: z.number().int(),
  duration_seconds: z.number().int(),
});
export type CoachingSessionSummary = z.infer<typeof CoachingSessionSummarySchema>;

export const PendingCoachingEditSchema = z.object({
  proposal_uuid: z.string().uuid(),
  accomplishment_uuid: z.string().uuid(),
  work_experience_title: z.string(),
  before_text: z.string(),
  after_text: z.string(),
  session_uuid: z.string().uuid(),
});
export type PendingCoachingEdit = z.infer<typeof PendingCoachingEditSchema>;

export const DashboardStatusSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("resume_active"),
    session_uuid: z.string().uuid(),
    mode: CoachingModeSchema,
    questions_remaining: z.number().int(),
  }),
  z.object({
    kind: z.literal("pending_post_gen"),
    session_uuid: z.string().uuid(),
    question_count: z.number().int(),
    company_name: z.string(),
  }),
  z.object({
    kind: z.literal("eligible_standalone"),
    sessions_today: z.number().int(),
    sessions_remaining_today: z.number().int(),
  }),
  z.object({ kind: z.literal("none") }),
]);
export type DashboardStatus = z.infer<typeof DashboardStatusSchema>;
